import { useCallback, useEffect, useState } from 'react'

import { type Message, MessageSort } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { z } from 'zod'

import { Page } from '@/components/layout/page/page.tsx'
import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { SortOptionsSelect } from '@/components/ui/table/components/sort-options-select.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { $creatorId } from '@/models/creators'
import { SettingsPageTitle } from '@/modules/creator-settings/SettingsPageTitle'
import { FollowUpMessagesPageSkeleton } from '@/modules/creator-settings/follow-up-messages/components/follow-up-messages-page-skeleton'
import { useFollowUpMessageSettings } from '@/modules/creator-settings/follow-up-messages/query/use-follow-up-message-settings'
import { generateFollowUpTableOptions } from '@/modules/creator-settings/follow-up-messages/utils.ts'
import { TimeInputSection } from '@/modules/expiring-fans/components/time-input-section'
import { DefaultSchemaBase } from '@/modules/message/components/editor/schema'
import { MessageSequenceEditor } from '@/modules/message/components/sequence/message-sequence-editor'
import { MessageContextType } from '@/modules/message/components/sequence/types.ts'
import { EmptyMessages } from '@/modules/online-mass-messages/ui/error/empty-messages'
import { handleError } from '@/utils/error'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'
import { useDebouncedProperty } from '@/utils/useDebouncedProperty'

const dialogTitle = 'Add a Follow-Up Message'
const dialogDescription = (
  <>
    Use the button to insert the fan’s name, which will replace the label with
    their actual name. If the fan doesn’t have a name, the label will be
    replaced with the fallback name.
  </>
)

const editorComponents = [
  'text',
  'media',
  'price',
  'fallback',
  'customAndFanNameLabels',
] as const

const Schema = DefaultSchemaBase.extend({ sequenceId: z.number().optional() })
const EditorResult = Schema.extend({ text: z.string().optional().nullish() })
const resolver = zodResolver(Schema)

const sortOptions = {
  Default: MessageSort.DEFAULT,
  Sent_To: MessageSort.SENT_TO,
  Reads: MessageSort.READS,
  Responses: MessageSort.RESPONSES,
  PPVs_Bought: MessageSort.PPV_BOUGHT,
}

export const FollowUpMessagesPage = () => {
  const [sortOption, setSortOption] = useState<MessageSort>(MessageSort.DEFAULT)

  const {
    isFetching,
    isUpdating,
    enabled,
    changeEnabled,
    notSeenEnabled,
    changeNotSeenEnabled,
    sendPurchasedPPVFollowUps,
    delay,
    messages,
    changeDelay,
    createMessage,
    changeMessage,
    deleteMessage,
    changeSendPurchasedPPVFollowUps,
  } = useFollowUpMessageSettings(sortOption)

  const toast = useToast()
  const creatorId = useUnit($creatorId)

  useEffect(() => {
    if (enabled && messages.length) {
      trackFeatureUsage(TrackedFeatures.AutoFollowUpMessages, creatorId)
    }
  }, [messages, enabled, creatorId])

  const handleSettingsUpdate = useCallback(
    (task: Promise<unknown>) => {
      task
        .then(() => toast.success('Settings updated'))
        .catch((error) => {
          handleError(error, 'Failed to update settings')
          console.error('Failed to update settings', error)
        })
    },
    [toast],
  )

  const handleChange = useCallback(
    (message: Message) => {
      const { sequenceId, text, sorting, ...rest } = EditorResult.parse(message)

      if (!sequenceId) {
        return Promise.reject(new Error('Invalid message'))
      }

      return changeMessage({
        ...rest,
        id: sequenceId,
        text: text ?? '',
        sorting: sorting ?? 0,
      } as Message)
    },
    [changeMessage],
  )

  const handleToggle = useCallback(() => {
    handleSettingsUpdate(changeEnabled(!enabled))
  }, [handleSettingsUpdate, changeEnabled, enabled])

  const handleNotSeenToggle = useCallback(() => {
    handleSettingsUpdate(changeNotSeenEnabled(!notSeenEnabled))
  }, [handleSettingsUpdate, changeNotSeenEnabled, notSeenEnabled])

  const handleSendPurchasedPPVFollowUps = useCallback(() => {
    handleSettingsUpdate(
      changeSendPurchasedPPVFollowUps(!sendPurchasedPPVFollowUps),
    )
  }, [
    handleSettingsUpdate,
    changeSendPurchasedPPVFollowUps,
    sendPurchasedPPVFollowUps,
  ])

  const handleDelayChange = useCallback(
    (delay: number) => {
      handleSettingsUpdate(changeDelay(delay))
    },
    [handleSettingsUpdate, changeDelay],
  )

  const [delayValue, setDelayValue] = useDebouncedProperty(
    delay,
    handleDelayChange,
  )

  const handleDelete = useCallback(
    (script: unknown) => {
      // The sequenceId is passed to the message sequence editor so we should
      // be able to parse it here
      const { sequenceId } = EditorResult.parse(script)

      if (!sequenceId) {
        return Promise.reject(new Error('Invalid message'))
      }

      return deleteMessage(sequenceId)
    },
    [deleteMessage],
  )

  if (isFetching) {
    return <FollowUpMessagesPageSkeleton />
  }

  return (
    <Page>
      <SettingsPageTitle
        title="PPV Follow-Ups"
        toggleName="enable-follow-up-message"
        onChange={handleToggle}
        enabled={enabled}
        description={
          <>
            Automatically follow up with fans after sending Pay-Per-View
            messages to boost your reply rate and increase conversions.
          </>
        }
      />
      {enabled && (
        <div>
          <Separator className="mt-8" />
          <TimeInputSection
            title="Set Time"
            description="Set how many minutes to wait before the follow-up message is sent after the Pay-Per-View."
            unit="Minutes"
            textClassName="max-w-[134px]"
            value={delayValue ?? 0}
            disabled={isUpdating}
            onChange={setDelayValue}
          />
          <Separator />
          <div className="text-title flex items-center gap-2 py-8 font-medium">
            <input
              type="checkbox"
              onChange={handleNotSeenToggle}
              checked={notSeenEnabled}
              className="accent-primary-500"
            />
            Include fans in your follow-up message who haven’t seen the PPV.
          </div>
          <Separator />
          <div className="text-title flex items-center gap-2 py-8 font-medium">
            <input
              type="checkbox"
              onChange={handleSendPurchasedPPVFollowUps}
              checked={sendPurchasedPPVFollowUps}
              className="accent-primary-500"
            />
            Include fans in your follow-up message who bought the PPV but
            haven’t replied.
          </div>
          <Separator />
          <EmptyProvider
            render={() => <EmptyMessages action="PPV Follow-up" />}
          >
            <MessageSequenceEditor
              title="Messages"
              description="Each follow-up message will be sent only once to each fan."
              placeholder="Hey %name%, how are you?"
              addMessageLabel="PPV Follow-Up"
              dialogTitle={dialogTitle}
              dialogDescription={dialogDescription}
              components={editorComponents}
              schema={resolver}
              entries={messages}
              // @ts-ignore
              onCreate={createMessage}
              onUpdate={handleChange}
              onRemove={handleDelete}
              submitError={
                createMessage._queryError || changeMessage._queryError
              }
              isRemoving={deleteMessage._isLoading}
              isSubmitting={
                createMessage._isLoading || changeMessage._isLoading
              }
              type={MessageContextType.FollowUpMessage}
              actionsDropDown
              generatePreviewIndicators={generateFollowUpTableOptions}
              sortOptions={
                <SortOptionsSelect
                  sortOptions={sortOptions}
                  sortOption={sortOption}
                  handleSortChange={setSortOption}
                />
              }
            />
          </EmptyProvider>
        </div>
      )}
    </Page>
  )
}
